import { useNavigate } from 'react-router-dom';
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Iconify from 'src/components/Iconify';
import { Sheet, SheetContent, SheetTrigger } from 'src/@/components/ui/sheet';
import { SidebarContext, SidebarMenuButton, SidebarGroup } from 'src/components/dashboard/SidebarMenuButton';
import { META_ADS_DISPONIVEL } from 'src/utils/metaAppReview';

// "Métricas" nao entra aqui: so funciona com ?campanha=<id>, acessada a partir
// de um card especifico em Rastreamento (ver MarketingRastreamento.js) — como
// item solto na sidebar ela cai direto em "Campanha nao encontrada".
// Grupos no mesmo padrao do SidebarDashboard (SidebarGroup com label).
const SIDEBAR_GROUPS = [
    {
        id: 'content',
        labelKey: 'sidebar.groups.content',
        items: [
            { id: '1', slug: 'agendamentos', nameKey: 'sidebar.schedules', icon: 'solar:clock-circle-bold' },
            // Campanhas = disparo WhatsApp/SMS (chats/Campanhas) — NAO e Meta Ads.
            { id: '2', slug: 'campanhas', nameKey: 'sidebar.whatsappCampaigns', icon: 'solar:flag-bold' },
        ]
    },
    {
        id: 'ads',
        labelKey: 'sidebar.groups.ads',
        items: [
            // Publicacoes = posts FB/IG via Graph API — e Meta, nao conteudo WhatsApp.
            { id: '5', slug: 'publicacoes', nameKey: 'sidebar.posts', icon: 'eva:calendar-fill' },
            { id: '3', slug: 'rastreamento', nameKey: 'sidebar.tracking', icon: 'eva:bar-chart-fill' },
            { id: '7', slug: 'ctwa', nameKey: 'sidebar.ctwa', icon: 'ic:round-whatsapp', isNew: true },
            // { id: '4', slug: 'analytics', nameKey: 'sidebar.analytics', icon: 'eva:activity-fill' },
            // Anuncios = Gestor de Anuncios Meta (Ads Manager), NAO templates WhatsApp.
            // Fora do menu enquanto a Meta nao liberar ads_read/pages_manage_ads
            // (ver metaAppReview): sem o escopo a tela nao tem dado nenhum.
            { id: '6', slug: 'anuncios', nameKey: 'sidebar.metaAds', icon: 'mdi:facebook', disponivel: META_ADS_DISPONIVEL },
        ]
    },
];

export default function MarketingSidebar() {
    const navigate = useNavigate();
    const { t } = useTranslation('marketing');
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);

    const isExpanded = !isCollapsed || isHovered;

    const sidebarGroups = useMemo(
        () => SIDEBAR_GROUPS.map((group) => ({
            ...group,
            label: t(group.labelKey),
            items: group.items
                .filter((item) => item.disponivel !== false)
                .map((item) => ({ ...item, nome: t(item.nameKey) }))
        })).filter((group) => group.items.length > 0),
        [t]
    );

    const goTo = (slug, onNavigate) => {
        navigate(`/dashboard/marketing/${slug}`);
        onNavigate?.();
    };

    const renderNavContent = (onNavigate) => (
        <nav>
            {sidebarGroups.map((group) => (
                <SidebarGroup key={group.id} label={group.label}>
                    {group.items.map((item) => (
                        <SidebarMenuButton
                            key={item.id}
                            title={item.nome}
                            path={`/dashboard/marketing/${item.slug}`}
                            icon={item.icon}
                            tag={item.isNew ? t('sidebar.newBadge') : undefined}
                            onClick={() => goTo(item.slug, onNavigate)}
                        />
                    ))}
                </SidebarGroup>
            ))}
        </nav>
    );

    return (
        <>
            {/* Mobile: hamburger + Sheet drawer */}
            <div className="md:hidden sticky top-0 z-40 bg-white border-b border-gray-100 px-4 py-2">
                <SidebarContext.Provider value={{ isExpanded: true }}>
                    <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                        <SheetTrigger asChild>
                            <button className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                                <Iconify icon="solar:hamburger-menu-linear" width={20} />
                                <span>{t('sidebar.title')}</span>
                            </button>
                        </SheetTrigger>
                        <SheetContent side="left" className="w-[300px] p-0 pt-8">
                            <div className="flex flex-col flex-1 overflow-y-auto py-2 px-4">
                                {renderNavContent(() => setMobileOpen(false))}
                            </div>
                        </SheetContent>
                    </Sheet>
                </SidebarContext.Provider>
            </div>

            {/* Desktop: collapsible aside, same pattern as SidebarDashboard */}
            <SidebarContext.Provider value={{ isExpanded }}>
                <aside
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    className={`
                        hidden md:flex flex-col flex-shrink-0 border-r border-gray-100 bg-white h-full relative z-20
                        transition-all duration-300 ease-in-out
                        ${isExpanded ? 'w-[280px]' : 'w-[80px]'}
                    `}
                >
                    <button
                        onClick={() => setIsCollapsed(!isCollapsed)}
                        className="
                            absolute top-1/2 -translate-y-1/2 -right-3 z-50
                            flex items-center justify-center w-6 h-6 rounded-full
                            bg-white border border-gray-200 shadow-sm
                            text-gray-500 hover:text-gray-900 hover:bg-gray-50
                            transition-all duration-200
                            cursor-pointer
                        "
                        title={isCollapsed ? t('common.pinMenu') : t('common.collapseMenu')}
                    >
                        <Iconify
                            icon={isCollapsed ? "solar:pin-bold" : "solar:alt-arrow-left-linear"}
                            width={14}
                            height={14}
                            className={`transition-transform duration-200 ${isCollapsed ? 'rotate-45' : ''}`}
                        />
                    </button>

                    <div className={`flex flex-col flex-1 overflow-y-auto py-4 scrollbar-thin scrollbar-thumb-gray-100 hover:scrollbar-thumb-gray-200 ${isExpanded ? 'px-4' : 'px-2'}`}>
                        {renderNavContent()}
                    </div>
                </aside>
            </SidebarContext.Provider>
        </>
    );
}
